/*
 * Copyright (c) 2021, salesforce.com, inc.
 * All rights reserved.
 * SPDX-License-Identifier: BSD-3-Clause
 * For full license text, see the LICENSE file in the repo root or https://opensource.org/licenses/BSD-3-Clause
 */
import React from 'react'
import PropTypes from 'prop-types'
import {FormattedMessage} from 'react-intl'
import {Box, Text, useMultiStyleConfig} from '@salesforce/retail-react-app/app/components/shared/ui'

const AddressDisplay = ({
    address,
    showAddressDetailsInline,
    isBillingAddress = false,
    isShippingAddress = false,
    hideFields,
    isOrderReview = false,
    ...props
}) => {
    const styles = useMultiStyleConfig('AddressDisplay', {variant: isOrderReview ? 'orderReview' : undefined})
    const apartment = address?.c_apartment || address?.apartment || address?.address2

    return (
        <Box data-private {...styles.container} {...props}>
            {address.preferred && (
                <Text {...styles.defaultLabel}>
                    <Text as='span' {...styles.defaultLabelBorder}>
                        <FormattedMessage
                            defaultMessage="Default Address"
                            id="address_display.label.default_address"
                        />
                    </Text>
                </Text>
            )}
            {address.c_addressLabel && (
                <Text {...styles.addressLabel}>{address.c_addressLabel}</Text>
            )}
            {!hideFields?.name && (
                <Text {...styles.name}>
                    {address.firstName} {address.lastName}
                </Text>
            )}
            {showAddressDetailsInline ? (
                <>
                    <Text {...styles.addressLine}>
                        {[address.address1, apartment, address.city].filter(Boolean).join(', ')}, {address.stateCode} {address.postalCode}
                        , {address.countryCode}
                    </Text>
                    {!hideFields?.phone && <Text {...styles.addressLine}>{address.phone}</Text>}
                </>
            ) : (
                <>
                    {isBillingAddress ? (
                        <>
                            <Text {...styles.addressLine}>
                                {address.address1}{apartment && `, ${apartment}`}, {address.city}, {address.stateCode},{' '}
                                {address.postalCode}
                            </Text>
                        </>
                    ) : isShippingAddress ? (
                        <>
                            <Text {...styles.addressLine}>
                                {address.address1}{apartment && `, ${apartment}`}, {address.city}, {address.stateCode},{' '}
                                {address.postalCode}
                            </Text>
                        </>
                    ) : (
                        <Text {...styles.addressLine}>
                            {address.address1}{apartment && `, ${apartment}`}, {address.city}, {address.stateCode}{' '}
                            {address.postalCode}
                        </Text>
                    )}
                </>
            )}
        </Box>
    )
}

AddressDisplay.propTypes = {
    address: PropTypes.object,
    showAddressDetailsInline: PropTypes.bool,
    isBillingAddress: PropTypes.bool,
    isShippingAddress: PropTypes.bool,
    hideFields: PropTypes.object,
    isOrderReview: PropTypes.bool
}

export default AddressDisplay
